---
title: Side agents
lede: More agents in the same session, each in a conversation of its own and a tab of its own on the stage.
order: 15
part: Run a task
---

A side agent is a second agent in the same session: same machine, same files, but a separate conversation from the session's own agent. Use one to ask a question or try something without filling the main conversation.

Side agents are tabs of the stage, listed in the session's surfaces column at the window's right, under the session's agent. Hover a cell for its name; a small flag on it gives its initials and number, such as `CC2`. The dock below holds shells only.

![The + at the end of the Surfaces column, with its New agent menu](media/side-agents-column.webp)

### Opening one

1. Press **+** at the end of the surfaces column.
2. With both agents turned on, pick one from the **New agent** menu. The session's own agent is listed first. With one agent on, the **+** opens it at once.

The tab is named after the agent, numbered from the second one: the session's agent counts as the first, so the next Claude Code is **Claude Code 2**. Drag the tab into another group to watch it beside the session's agent.

### Ending one

Select its row and press `Delete`, or right-click it and pick **Delete agent**. An agent at rest ends at once. One that is working asks first: **End agent** stops its turn where it is, and what it already wrote in the sandbox stays. **Keep it** leaves it running.

Every side agent ends when its session closes.

### What a side agent changes

It edits the same files as the session's agent, so its edits are part of the session's diff and are merged with the rest. Its turns are not the session's, though: the session's status and notifications follow the session's own agent.

### Agents Airlock opens for you

Three kinds of side agent open on their own, with a name that says why.

| Tab | Opened by | Closes |
|---|---|---|
| **Claude Code · <step>** | A workflow step set to run in a **New** or **Named** conversation | When you end it, or with the session |
| **Claude Code · <name>** | The session's agent, to work a piece of the task in parallel | When you end it, or with the session |
| **Claude Code** (from the shell) | Clicking the `↳ … · conversation …` line under an answer in the VM shell | When you end it, or with the session |

A step's agent and an agent started by the session's agent open in a group to the right of the session's agent, so you can watch both.

### Step agents

A workflow step set to run in a **New** or **Named** conversation runs in its own side agent, named after the step. It reads what the steps before it reported and the change, but none of the reasoning that produced them, which is what a review step wants.

The step agent's turns count as the session's while the step runs, and its report moves the workflow on. Coming back to a **New** step starts a new conversation; steps that share a **Named** conversation come back to the same one. If the step agent's terminal goes away, the run waits on you; **Launch it again** opens it. See [Workflows](/workflows).

### Agents the agent starts

The session's agent can start up to three agents of its own at once, twelve over the session's life, each on a piece of the task. Each writes a report when it is done, and the session's agent waits for those reports before it hands back. See [Agents that run agents](/agents-run-agents).
